'use client';

import React from 'react';
import { Pencil, ExternalLink, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Client } from './ClientTeamContent';


interface Props {
  clients: Client[];
}

const healthConfig = {
  good: { icon: CheckCircle2, color: 'text-positive', label: 'Healthy' },
  warning: { icon: AlertTriangle, color: 'text-warning', label: 'Needs attention' },
  critical: { icon: AlertCircle, color: 'text-negative', label: 'Critical issues' },
};

const statusVariant: Record<Client['status'], string> = {
  active: 'badge-active',
  onboarding: 'badge-review',
  paused: 'badge-paused',
};

export default function ClientsTable({ clients }: Props) {
  return (
    <div className="card-base overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Industry</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pages</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active Campaigns</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Monthly Spend</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Health</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Manager</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Since</th>
              <th className="px-4 py-3 w-10"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {clients.map((client) => {
              const health = healthConfig[client.health];
              const HealthIcon = health.icon;
              return (
                <tr key={client.id} className="table-row-hover group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-primary">{client.name.charAt(0)}</span>
                      </div>
                      <span className="font-medium text-foreground">{client.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">{client.industry}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium text-foreground tabular-nums">{client.connectedPages}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-medium tabular-nums ${client.activeCampaigns === 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {client.activeCampaigns}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold tabular-nums text-foreground">
                      {client.monthlySpend > 0 ? `$${client.monthlySpend.toLocaleString()}` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <HealthIcon size={13} className={health.color} />
                      <span className={`text-xs font-medium ${health.color}`}>{health.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={statusVariant[client.status]}>
                      {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">{client.manager}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground">{client.since}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <button
                        onClick={() => toast.info(`Editing ${client.name}`)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                        title="Edit client"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => toast.info(`Viewing ${client.name} details`)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                        title="View client details"
                      >
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}